import { Box, Chevron, type EnhancedDimension } from '@factorialco/deprecated-design-system'
import cn from 'classnames/bind'
import { AnimatePresence, motion } from 'framer-motion'

import { formatDateRange } from 'modules/core/components/DateNavigator/dateFormatUtils'
import { usePrevious } from 'modules/core/hooks/usePrevious'
import i18n from 'modules/core/lib/i18n'

import styles from './index.module.css'

const cx = cn.bind(styles)
interface Props {
  range: [Date, Date]
  minDate?: Date
  maxDate?: Date
  onPrevRange: () => void
  onNextRange: () => void
  minWidth?: EnhancedDimension
  animate?: boolean
}

export default function DateRangeNavigator({
  range,
  onPrevRange,
  onNextRange,
  minDate,
  maxDate,
  minWidth,
  animate = false,
}: Props) {
  const enablePrevious = !minDate || minDate < range[0]
  const enableNext = !maxDate || maxDate > range[1]

  const previousRangeRef = usePrevious(range)

  const goToThePast = previousRangeRef && previousRangeRef[0] > range[0]
  const goToTheFuture = previousRangeRef && previousRangeRef[0] < range[0]

  let xTransition = 0

  if (goToThePast) {
    xTransition = -40
  } else if (goToTheFuture) {
    xTransition = 40
  }

  const formattedString = formatDateRange(range[0], range[1], false, false)

  return (
    <div className={styles.dateRangeNavigator}>
      <div
        className={cx('arrow', {
          hidden: !enablePrevious,
        })}
      >
        <button
          className={styles.button}
          onClick={onPrevRange}
          aria-label={i18n.t('common.month_navigator.previous_month')}
        >
          <Chevron type="secondary" direction="left" />
        </button>
      </div>
      <Box minWidth={minWidth} alignItems="center">
        {animate ? (
          <AnimatePresence mode="popLayout">
            <motion.div
              key={range[0].toISOString()}
              initial={{ x: xTransition, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ duration: 0.3 }}
            >
              <div className={styles.caption}>{formattedString}</div>
            </motion.div>
          </AnimatePresence>
        ) : (
          <div className={styles.caption}>{formattedString}</div>
        )}
      </Box>
      <div
        className={cx('arrow', {
          hidden: !enableNext,
        })}
      >
        <button
          className={styles.button}
          onClick={onNextRange}
          aria-label={i18n.t('common.month_navigator.next_month')}
        >
          <Chevron type="secondary" direction="right" />
        </button>
      </div>
    </div>
  )
}
